import { useQueryClient } from "@tanstack/react-query";
import {
  getQueryKey,
  useFetchData,
  useOrgId,
} from "@/lib/GraphQLCodegen/fetcher";
import { threadsInfiniteList } from "@/app/Threads/GraphQL/threadQueries";
import {
  threadListNextPageParam,
  threadListVariables,
} from "@/app/Threads/hooks/useThreads";
import { ThreadsInfiniteListQueryVariables } from "@/gql/graphql";

/**
 * The thread a list puts at the top. Reads the same cache entry the list itself
 * does, so it answers immediately once that list has loaded and shares the
 * request when it hasn't.
 */
export function useFirstThreadId() {
  const queryClient = useQueryClient();
  const fetcher = useFetchData(threadsInfiniteList);
  const orgId = useOrgId();

  return async (
    variables?: Partial<ThreadsInfiniteListQueryVariables>,
  ): Promise<string | undefined> => {
    const listVariables = threadListVariables(variables);

    // fetchInfiniteQuery honours staleTime, so a fresh list answers from cache
    // and one invalidated by a close is awaited. ensureInfiniteQueryData returns
    // the stale pages either way, which is the wrong thread to move to.
    const data = await queryClient.fetchInfiniteQuery({
      queryKey: getQueryKey(threadsInfiniteList, listVariables, orgId, true),
      queryFn: ({ pageParam }) => fetcher({ ...listVariables, ...pageParam }),
      initialPageParam: { page: 1 },
      getNextPageParam: threadListNextPageParam,
    });

    return data.pages[0]?.threads?.data?.[0]?.id;
  };
}
